import { useQuery } from "@tanstack/react-query";
import { organizationClientContract } from "@common/contracts/organization-client.contract";
import {
    ListOrganizationMemberClientAccessQuery,
    ListOrganizationMemberClientAccessResponse,
} from "@common/schemas/organization-member-client-access.schema";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { keepPreviousDataOnPageChange } from "./keepPreviousDataOnPageChange";

const organizationClientContractClient = getBackendAPIClient(
    organizationClientContract,
);

export const organizationMemberAccessLogQueryKeys = {
    all: () => ["organization-member-access-log"],
    list: (
        organizationId: string,
        query: ListOrganizationMemberClientAccessQuery,
    ) => [...organizationMemberAccessLogQueryKeys.all(), organizationId, query],
} as const;

interface UseOrganizationMemberAccessLogParams {
    organizationId: string;
    query: ListOrganizationMemberClientAccessQuery;
    enabled?: boolean;
}

const EMPTY_RESULT: ListOrganizationMemberClientAccessResponse = {
    data: [],
    total: 0,
    page: 1,
    pageCount: 1,
};

/**
 * Paginated, filterable, sortable read of an organization's member
 * client-access audit log. Server-scoped to `organizationId` and gated by
 * `@AdminAccess()` on the backend (org admins/owners only) — this hook is
 * only reachable from UI already gated by
 * `useCanViewOrganizationMemberAccessLog`.
 */
export function useOrganizationMemberAccessLog({
    organizationId,
    query,
    enabled = true,
}: UseOrganizationMemberAccessLogParams) {
    const queryKey = organizationMemberAccessLogQueryKeys.list(
        organizationId,
        query,
    );
    return useQuery({
        queryKey,
        queryFn: async () => {
            if (!organizationId) {
                return EMPTY_RESULT;
            }
            const result =
                await organizationClientContractClient.listClientAccess({
                    params: { organizationId },
                    query,
                });
            return result.body;
        },
        enabled: enabled && !!organizationId,
        placeholderData: keepPreviousDataOnPageChange(queryKey),
    });
}
